//src/components/CreateProductionPlan.jsx :-
import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  CircularProgress,
  Autocomplete,
} from "@mui/material";
import { useNavigate } from "react-router-dom"; // Import useNavigate hook
import { Close, RemoveCircle, AddCircle } from "@mui/icons-material"; // Import AddCircle for the + icon
import { grey } from "@mui/material/colors";
import axios from "axios";
import env from "react-dotenv";
import AutorenewIcon from '@mui/icons-material/Autorenew';
import DateRangeIcon from '@mui/icons-material/DateRange'; // Import DateRange icon

const CreateProductionPlan = ({ isOpen, onClose, onSave }) => {
  const navigate = useNavigate(); // Hook to navigate between pages
  const [productList, setProductList] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [machineList, setMachineList] = useState([]);
  const [consigneeList, setConsigneeList] = useState([]);
  const [productionDate, setProductionDate] = useState("");
  const [planned_quantity, setplanned_quantity] = useState("");
  const [machines, setMachines] = useState([{ machine: "", madeQuantity: "", startDate: "", endDate: "", isHalf: false }]);
  const [consignees, setConsignees] = useState([{ consignee: "", orderPON: "", quantity: "", deliveryDate: "", dispatchDate: "" }]);
  const [loading, setLoading] = useState(false);
  const [noMachinesMessage, setNoMachinesMessage] = useState("");
  const [errorMachines, setErrorMachines] = useState([]); // State to track machines with errors

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch product list
        const productResponse = await axios.get(`${process.env.REACT_APP_PJCBACKEND_URL}/api/main-product`);
        setProductList(productResponse.data);

        // Fetch machine list
        const machineResponse = await axios.get(`${process.env.REACT_APP_PJCBACKEND_URL}/api/machinelist`);
        // console.log("Fetched machine list:", machineResponse.data);
        setMachineList(machineResponse.data);

        // Fetch consignee list
        const consigneeResponse = await axios.get(`${process.env.REACT_APP_PJCBACKEND_URL}/api/consignees`);
        setConsigneeList(consigneeResponse.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    // Fetch machines for the selected product when it changes
    const fetchMachinesForProduct = async () => {
      if (!selectedProduct) {
          setMachineList([]); // Reset machine list if no product is selected
          setNoMachinesMessage(""); // Clear any previous message
          return;
      }
  
      try {
          // Fetch machines for the selected product using the new endpoint
          const response = await axios.get(
              `${process.env.REACT_APP_PJCBACKEND_URL}/api/productwisemachine/filter/${selectedProduct.id}/machines`
          );
  
          // Check if the response is an array and flatten it if necessary
          const machines = Array.isArray(response.data) ? response.data.flat() : [];
  
          if (machines.length === 0) {
              // If no machines are found, display a message
              setMachineList([]); // Ensure machineList is empty
              setNoMachinesMessage("No machines are added for the selected product.");
          } else {
              setMachineList(machines);
              setNoMachinesMessage(""); // Clear the message
          }
      } catch (error) {
          console.error("Error fetching machines for selected product:", error);
          setMachineList([]); // Reset the machine list on error
          setNoMachinesMessage("No machines are available for the selected product."); // Show error message
      }
  };
  
    fetchMachinesForProduct();
  }, [selectedProduct]);

const handleDateChange = async (index, field, value) => {
  const updatedMachines = [...machines];
  updatedMachines[index][field] = value;

  const { startDate, endDate } = updatedMachines[index];

  if (startDate && endDate) {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const workingHoursPerDay = 22;

    if (!isNaN(start) && !isNaN(end) && start <= end) {
      const dayDifference = Math.floor((end - start) / (1000 * 60 * 60 * 24)) + 1; // include both days
      updatedMachines[index].madeQuantity = updatedMachines[index].initialMadeQuantity * workingHoursPerDay * dayDifference || 0;
      // console.log("Day Difference:", dayDifference, "→ Made Quantity:", updatedMachines[index].madeQuantity);

      // Recalculate inCost based on the updated dates
      const machineId = updatedMachines[index].machine.machine_id; // Get the machine ID
      const productId = selectedProduct.id; // Get the selected product ID
      const quantity = updatedMachines[index].madeQuantity; // Use the calculated madeQuantity
    try {
        // Fetch cost_unit of raw materials
        const costResponse = await axios.get(
          `${process.env.REACT_APP_PJCBACKEND_URL}/api/productwisemachine/cost/${machineId}?productId=${productId}`
        );
        const costUnit = costResponse.data.cost_unit; // Assuming the response has cost_unit
        // Calculate inCost
        const inCost = costUnit * quantity;
        updatedMachines[index].inCost = inCost; // Update inCost
      } catch (error) {
        console.error("Error fetching cost:", error);
        updatedMachines[index].inCost = 0; // Reset inCost on error
      }
    }
  } else {
    // updatedMachines[index].madeQuantity = updatedMachines[index].initialMadeQuantity;
    // If either date is cleared, revert inCost to the original based on quantity
    const originalQuantity = updatedMachines[index].originalInitialMadeQuantity || 0; // Use original quantity
    const machineId = updatedMachines[index].machine.machine_id; // Get the machine ID
    const productId = selectedProduct.id; // Get the selected product ID
    try {
      // Fetch cost_unit of raw materials
      const costResponse = await axios.get(
        `${process.env.REACT_APP_PJCBACKEND_URL}/api/productwisemachine/cost/${machineId}?productId=${productId}`
      );
      const costUnit = costResponse.data.cost_unit; // Assuming the response has cost_unit
      // Calculate inCost based on original quantity
      const inCost = costUnit * originalQuantity;
      updatedMachines[index].inCost = inCost; // Update inCost to original
    } catch (error) {
      console.error("Error fetching cost:", error);
      updatedMachines[index].inCost = 0; // Reset inCost on error
    }
    updatedMachines[index].madeQuantity = originalQuantity; // Reset madeQuantity to original
  }

  setMachines(updatedMachines);
};

const handleToggleMadeQuantity = async (index) => {
  const updatedMachines = [...machines];
  const currentMachine = updatedMachines[index];

    // Check if both startDate and endDate are selected
  if (currentMachine.startDate && currentMachine.endDate) {
  // Toggle the isHalf flag
  currentMachine.isHalf = !currentMachine.isHalf;

  // Calculate new initial quantity based on the isHalf state
  const newInitial = currentMachine.isHalf
    ? currentMachine.initialMadeQuantity / 2
    : currentMachine.originalInitialMadeQuantity || currentMachine.initialMadeQuantity;

  currentMachine.initialMadeQuantity = newInitial;

  // Recalculate madeQuantity if dates are selected
  if (currentMachine.startDate && currentMachine.endDate) {
    const start = new Date(currentMachine.startDate);
    const end = new Date(currentMachine.endDate);
    const workingHoursPerDay = 22;

    if (!isNaN(start) && !isNaN(end) && start <= end) {
      const dayDifference = Math.floor((end - start) / (1000 * 60 * 60 * 24)) + 1; // include both days
      currentMachine.madeQuantity = newInitial * workingHoursPerDay * dayDifference || 0;
    } else {
      currentMachine.madeQuantity = newInitial;
    }
  } else {
    currentMachine.madeQuantity = newInitial;
  }

  // Recalculate inCost based on the updated madeQuantity
  const machineId = currentMachine.machine.machine_id; // Get the machine ID
  const productId = selectedProduct.id; // Get the selected product ID
  const quantity = currentMachine.madeQuantity; // Use the updated madeQuantity
  try {
    // Fetch cost_unit of raw materials
    const costResponse = await axios.get(
      `${process.env.REACT_APP_PJCBACKEND_URL}/api/productwisemachine/cost/${machineId}?productId=${productId}`
    );
    const costUnit = costResponse.data.cost_unit; // Assuming the response has cost_unit
    // Calculate inCost
    const inCost = costUnit * quantity;
    currentMachine.inCost = inCost; // Update inCost
  } catch (error) {
    console.error("Error fetching cost:", error);
    currentMachine.inCost = 0; // Reset inCost on error
  }

  } else {
    alert("Please select both start and end dates before toggling the 12 hour format.");
  }

  setMachines(updatedMachines);
};

// console.log("Machine Data:- ", machines);

const handleMachineChange = async (index, newMachine) => {
  const updatedMachines = [...machines];
  updatedMachines[index].machine = newMachine;

  if (newMachine && newMachine.machine_id) {
      try {
          const machineId = newMachine.machine_id; // Correctly access the 'machine_id' property
          const productId = selectedProduct.id; // Get the selected product ID
          const response = await axios.get(
              `${process.env.REACT_APP_PJCBACKEND_URL}/api/productwisemachine/quantity/${machineId}?productId=${productId}`
          );
          // console.log("Response from quantity API:", response); // Log the entire response

          const { quantity } = response.data; // This should now correctly access the quantity

          // Fetch cost_unit of raw materials
          const costResponse = await axios.get(
            `${process.env.REACT_APP_PJCBACKEND_URL}/api/productwisemachine/cost/${machineId}?productId=${productId}`
          );
          const costUnit = costResponse.data.cost_unit; // Assuming the response has cost_unit
          // Calculate inCost
          const inCost = costUnit * quantity;

          updatedMachines[index].originalInitialMadeQuantity = quantity || 0; // store original for future recalculation
          updatedMachines[index].initialMadeQuantity = quantity || 0; // Store the initial capacity
          updatedMachines[index].madeQuantity = quantity || 0;
          updatedMachines[index].inCost = inCost; // Store inCost
      } catch (error) {
          console.error("Error fetching quantity:", error);
          updatedMachines[index].initialMadeQuantity = 0;
          updatedMachines[index].madeQuantity = 0;
          updatedMachines[index].inCost = 0; // Reset inCost on error
      }
  } else {
      console.warn("Machine ID is undefined or newMachine is not selected.");
      updatedMachines[index].initialMadeQuantity = 0;
      updatedMachines[index].madeQuantity = 0;
      updatedMachines[index].inCost = 0; // Reset inCost
  }

  setMachines(updatedMachines);
};
  
  const handleAddMachine = () => {
    // setMachines([...machines, { machine: "", madeQuantity: "", startDate: "", endDate: "" }]);
    setMachines([
      ...machines,
      {
        machine: "",
        madeQuantity: "",
        startDate: "",
        endDate: "",
        isHalf: false,
        initialMadeQuantity: 0,
        originalInitialMadeQuantity: 0
      }
    ]);
  };

  const handleRemoveMachine = (index) => {
    const newMachines = [...machines];
    newMachines.splice(index, 1);
    setMachines(newMachines);
  };

  const handleAddConsignee = () => {
    setConsignees([...consignees, { consignee: "", orderPON: "", quantity: "", deliveryDate: "", dispatchDate: "" }]);
  };

  const handleRemoveConsignee = (index) => {
    const newConsignees = [...consignees];
    newConsignees.splice(index, 1);
    setConsignees(newConsignees);
  };

  const handleSave = async () => {
    if (!selectedProduct || !productionDate || !planned_quantity) {
      alert("Product, Production date, and planned quantity are required!");
      return;
    }
  
    const selectedDate = new Date(productionDate);
    const production_year = selectedDate.getFullYear();
    const production_month = selectedDate.toLocaleString("default", { month: "long" });

    // Perform validation for each machine
      const errors = machines.map((machine, index) => {
        const madeQty = Number(machine.madeQuantity) || 0;
        const plannedQty = Number(planned_quantity) || 0;
        return madeQty < plannedQty ? index : null;
      }).filter(index => index !== null);

      if (errors.length > 0) {
        setErrorMachines(errors);
        alert("Please edit planned quantity, machine's quantity should be >= planned quantity.");
        return;
      } else {
        setErrorMachines([]); // Clear previous errors if none found
      }

  // Prepare machine availability check data
  const machineAvailabilityChecks = machines.map(machine => ({
    machineId: machine.machine.machine_id,
    machineName: machine.machine.MachineName, // Include the machine name
    startDate: machine.startDate,
    endDate: machine.endDate,
  }));
  // Check machine availability
  try {
    const availabilityResponse = await axios.post(`${process.env.REACT_APP_PJCBACKEND_URL}/api/production-plan/check-multiple-machines-availability`, { machines: machineAvailabilityChecks });
    // Map unavailable machines to their names
  const unavailableMachines = availabilityResponse.data
    .filter(result => !result.isAvailable)
    .map(result => {
      const machine = machineAvailabilityChecks.find(m => m.machineId === result.machineId);
      return machine ? machine.machineName : result.machineId; // Fallback to ID if name is not found
    });
    if (unavailableMachines.length > 0) {
    alert(`The following machines are already scheduled for the selected dates: ${unavailableMachines.join(", ")}`);
    return;
  }
  } catch (error) {
    console.error("Error checking machine availability:", error);
    alert("An error occurred while checking machine availability.");
    return;
  }
  
    // Structure the payload to include machines and consignees with the required fields
    const payload = {
      product_id: selectedProduct.id,
      production_year,
      production_month,
      product_name: selectedProduct.product_name,
      planned_quantity,
      machines: machines.map(machine => {
        // Log the selected machine object
        // console.log("Selected machine:", machine.machine); // Log the entire machine object
        // console.log("Selected machine ID:", machine.machine.machine_id); // Log the machine ID

        return {
          productId: selectedProduct.id,
          machineId: machine.machine.machine_id, // Use 'machine_id' here
          machine: machine.machine.MachineName, // Assuming machine object has a 'MachineName' property
          start_date: machine.startDate,
          end_date: machine.endDate,
          made_quantity: machine.madeQuantity,
          inCost: machine.inCost, // Include inCost in the payload
        };
      }),
      consignees: consignees.map(consignee => ({
        consignee_name: consignee.consignee.consignee_name, // Assuming consignee object has 'consignee_name'
        order_PON: consignee.orderPON,
        quantity: consignee.quantity,
        delivery_date: consignee.deliveryDate,
        dispatch_date: consignee.dispatchDate,
      })),
    };
  
    // console.log("Payload to save:", payload); // Log the payload before saving
    onSave(payload);
    handleClose(); // Close and reset on save
};

  const handleClose = () => {
    // Reset the form data to initial state
    setSelectedProduct(null);
    setProductionDate("");
    setplanned_quantity("");
    setMachines([{ machine: "", madeQuantity: "", startDate: "", endDate: "" }]);
    setConsignees([{ consignee: "", orderPON: "", quantity: "", deliveryDate: "", dispatchDate: "" }]);
    setErrorMachines([]); // Reset error tracking on close
    onClose(); // Close the modal
    window.location.reload();
    // window.location.href = "/production-plan";
    // navigate("/production-plan"); // Redirect to ProductionPlan.jsx
  };

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 900,
    bgcolor: "background.paper",
    border: `2px solid ${grey[300]}`,
    borderRadius: "10px",
    boxShadow: 24,
    p: 4,
  };

  const contentStyle = {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    maxHeight: "80vh", // Set maximum height for the content
    overflowY: "auto", // Enable vertical scrolling
  };

  const headerStyle = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px",
    borderBottom: `1px solid ${grey[400]}`,
    paddingBottom: "10px",
    width: "100%", // Make sure the container takes full width
  };
  
  const typographyStyle = {
    flexGrow: 1, // This will push the Typography to the center
    textAlign: "center", // Ensure the text is centered within its space
  };  

  const footerStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center", // Center buttons horizontally
    marginTop: "5px",
    borderTop: `1px solid ${grey[400]}`,
    paddingTop: "10px",
  };  

  return (
    <Modal open={isOpen} onClose={handleClose}>
      <Box sx={modalStyle}>
      <Box sx={headerStyle}>
        <Typography variant="h6" fontWeight="bold" sx={typographyStyle}>
          Create Production Plan
        </Typography>
        <IconButton onClick={handleClose}>
          <Close />
        </IconButton>
      </Box>
        <Box sx={contentStyle}> {/* Apply the new content style here */}
          <Autocomplete
            options={productList}
            getOptionLabel={(option) => option.product_name || ""}
            value={selectedProduct}
            onChange={(event, newValue) => setSelectedProduct(newValue)}
            renderInput={(params) => (
              <TextField {...params} variant="outlined" label="Product Name" placeholder="Select a product" />
            )}
          />
          
          {/* Combine Production Date and Planned Quantity in one row */}
          <Box display="flex" gap={2}>
            <TextField
              label="Production Date"
              type="date"
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
              value={productionDate}
              onChange={(e) => setProductionDate(e.target.value)}
            />
            <TextField
              label="Planned Quantity"
              variant="outlined"
              fullWidth
              type="number"
              value={planned_quantity}
              onChange={(e) => setplanned_quantity(e.target.value)}
            />
          </Box>
          
{/* Machine Inputs with increased size */}
{noMachinesMessage && (
  <Typography
    variant="body1"
    color="error"
    sx={{ marginBottom: 2 }}
  >
    {noMachinesMessage}
  </Typography>
)}

{machines.map((machine, index) => (
  <Box
  key={index}
  display="flex"
  gap={2}
  sx={{
    backgroundColor: errorMachines.includes(index)
      ? "rgba(255, 0, 0, 0.1)" // light red background for error
      : "transparent",
    borderLeft: errorMachines.includes(index)
      ? "0px solid red" // red left border
      : "none",
    paddingLeft: errorMachines.includes(index) ? "8px" : "0",
    borderRadius: "4px",
    alignItems: "center",
  }}
>
  <Autocomplete
  options={machineList}
  getOptionLabel={(option) => option.MachineName || ""}
  value={machine.machine} // Ensure this is bound to the correct machine object
  onChange={(event, newValue) => {
    // console.log("New machine selected:", newValue); // Log the new value
    handleMachineChange(index, newValue);
  }}
  renderInput={(params) => (
    <TextField {...params} label="Machine" placeholder="Select a machine" sx={{ width: "200px" }} />
  )}
/>
    <TextField
      label="Qty"
      type="number"
      sx={{ width: "250px" }}
      value={machine.madeQuantity}
      // onChange={(e) => {
      //   const updatedMachines = [...machines];
      //   updatedMachines[index].madeQuantity = e.target.value;
      //   setMachines(updatedMachines);
      // }}
      placeholder="Qty"
      InputProps={{
        readOnly: true,
      }}
    />
    <TextField
      label="InCost"
      type="number"
      sx={{ width: "260px" }}
      value={machine.inCost || 0} // Display inCost
      placeholder="InCost"
      InputProps={{
        readOnly: true,
      }}
    />
    <TextField
      label="Start Date"
      type="date"
      variant="outlined"
      sx={{ width: "260px" }}
      InputLabelProps={{
        shrink: true,
      }}
      value={machine.startDate}
      onChange={(e) =>
        handleDateChange(index, "startDate", e.target.value)
      }
    />
    <TextField
      label="End Date"
      type="date"
      variant="outlined"
      sx={{ width: "250px" }}
      InputLabelProps={{
        shrink: true,
      }}
      value={machine.endDate}
      onChange={(e) =>
        handleDateChange(index, "endDate", e.target.value)
      }
    />
    <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
      <IconButton onClick={() => handleToggleMadeQuantity(index)} sx={{ p: "6px" }}>
        {machine.isHalf ? <DateRangeIcon color="primary" /> : <DateRangeIcon color="secondary" />}
      </IconButton>
      <IconButton onClick={() => handleRemoveMachine(index)} sx={{ p: "6px" }}>
        <RemoveCircle color="error" />
      </IconButton>
    </Box>
  </Box>
))}

          <Box display="flex" alignItems="center">
            {/* <IconButton onClick={handleAddMachine} sx={{ marginRight: "10px" }}>
              <AddCircle />
            </IconButton> */}
            <Button onClick={handleAddMachine} color="secondary">Add More Machine</Button>
          </Box>

          {/* Consignee Inputs with increased size */}
          {consignees.map((consignee, index) => (
            <Box display="flex" gap={2} key={index}>
              <Autocomplete
                options={consigneeList}
                getOptionLabel={(option) => option.consignee_name || ""}
                value={consignee.consignee}
                onChange={(event, newValue) => {
                  const newConsignees = [...consignees];
                  newConsignees[index].consignee = newValue;
                  setConsignees(newConsignees);
                }}
                renderInput={(params) => (
                  <TextField {...params} variant="outlined" label="Consignee Name" placeholder="Select a consignee" sx={{ width: '280px' }} />
                )}
              />
              <TextField
                label="PO No."
                variant="outlined"
                sx={{ width: '180px' }}
                value={consignee.orderPON}
                onChange={(e) => {
                  const newConsignees = [...consignees];
                  newConsignees[index].orderPON = e.target.value;
                  setConsignees(newConsignees);
                }}
              />
              <TextField
                label="Quantity"
                variant="outlined"
                sx={{ width: '220px' }}
                type="number"
                value={consignee.quantity}
                onChange={(e) => {
                  const newConsignees = [...consignees];
                  newConsignees[index].quantity = e.target.value;
                  setConsignees(newConsignees);
                }}
              />
              <TextField
                label="Delivery Date"
                type="date"
                variant="outlined"
                sx={{ width: '250px' }}
                InputLabelProps={{
                  shrink: true,
                }}
                value={consignee.deliveryDate}
                onChange={(e) => {
                  const newConsignees = [...consignees];
                  newConsignees[index].deliveryDate = e.target.value;
                  setConsignees(newConsignees);
                }}
              />
              <TextField
                label="Dispatch Date"
                type="date"
                variant="outlined"
                sx={{ width: '250px' }}
                InputLabelProps={{
                  shrink: true,
                }}
                value={consignee.dispatchDate}
                onChange={(e) => {
                  const newConsignees = [...consignees];
                  newConsignees[index].dispatchDate = e.target.value;
                  setConsignees(newConsignees);
                }}
              />
              <IconButton onClick={() => handleRemoveConsignee(index)}>
                <RemoveCircle color="error" />
              </IconButton>
            </Box>
          ))}
          <Box display="flex" alignItems="center">
            {/* <IconButton onClick={handleAddConsignee} sx={{ marginRight: "10px", color: "green"}}>
              <AddCircle />
            </IconButton> */}
            <Button onClick={handleAddConsignee} color="secondary">Add More Consignee</Button>
          </Box>

          <Box sx={footerStyle}>
            <Button color="secondary" onClick={handleClose} sx={{ marginRight: "10px" }}>Cancel</Button>
            <Button color="primary" onClick={handleSave} disabled={loading}>
              {loading ? <CircularProgress size={24} color="secondary" /> : "Save"}
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default CreateProductionPlan;
